import {FC, useState} from 'react'
import {PrintFlowOutcome} from '../printFlow/outcomeModel'
import {formatElapsedSince, formatTimestamp} from '../time'
import {PrintStoryDoc, PrintStoryEvent} from '../types'
import {ContentStoryTimeline} from './ContentStoryTimeline'

type Props = {
  outcome: PrintFlowOutcome
  storyEvents: PrintStoryEvent[]
  printJobs: PrintStoryDoc[]
  mishaps?: PrintStoryDoc[]
  onOpenJson?: () => void
}

export const SemanticTimeline: FC<Props> = ({
  outcome,
  storyEvents,
  printJobs,
  mishaps = [],
  onOpenJson,
}) => {
  const [showTechnical, setShowTechnical] = useState(false)
  const originTs = outcome.semanticEvents[0]?.ts || outcome.lastRealEventAt || Date.now()

  return (
    <div className='card card-flush border border-gray-200 mb-6'>
      <div className='card-header border-0 pt-5 pb-0'>
        <div className='card-title flex-column align-items-start'>
          <span className='fw-bold text-gray-800'>Timeline</span>
          <span className='text-muted fs-7 fw-semibold mt-1'>
            Semantic events only · scale ends at the latest real activity
          </span>
        </div>
      </div>
      <div className='card-body pt-5'>
        {outcome.semanticEvents.length === 0 ? (
          <div className='text-gray-700 fs-6'>No timed activity yet.</div>
        ) : (
          <div className='d-flex flex-column'>
            {outcome.semanticEvents.map((event, index) => {
              const isLast = index === outcome.semanticEvents.length - 1
              return (
                <div key={`${event.ts}-${event.title}-${index}`} className='d-flex gap-4'>
                  <div className='d-flex flex-column align-items-center' style={{width: 64}}>
                    <span className='badge badge-light-primary fw-bold fs-8 mb-1'>
                      {formatElapsedSince(event.ts, originTs)}
                    </span>
                    <span
                      className='rounded-circle bg-primary'
                      style={{width: 10, height: 10, zIndex: 1}}
                    />
                    {!isLast && (
                      <span
                        className='flex-grow-1'
                        style={{width: 2, minHeight: 28, background: '#e4e6ef', marginTop: 4}}
                      />
                    )}
                  </div>
                  <div className={isLast ? 'pb-0' : 'pb-5'}>
                    <div className='fw-bold text-gray-800'>{event.title}</div>
                    {event.detail && <div className='text-gray-700 fs-6 mt-1'>{event.detail}</div>}
                    <div className='text-gray-700 fs-7 mt-1'>{formatTimestamp(event.ts)}</div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        <div className='mt-6'>
          <button
            type='button'
            className='btn btn-sm btn-light me-2'
            onClick={() => setShowTechnical((v) => !v)}
          >
            {showTechnical ? 'Hide' : 'Show'} technical timeline
          </button>
          {onOpenJson && (
            <button type='button' className='btn btn-sm btn-light-primary' onClick={onOpenJson}>
              Open JSON / technical evidence
            </button>
          )}
        </div>

        {showTechnical && (
          <div className='mt-5'>
            <ContentStoryTimeline
              events={storyEvents}
              printJobs={printJobs}
              mishaps={mishaps}
              endAtLastEvent
            />
          </div>
        )}
      </div>
    </div>
  )
}
